import { MutationTree } from 'vuex';
import {
    AccountInventoryResultState,
    AccountInventoryResult,
    AccountInventoryResultBasicInformation,
    AccountInventoryResultDetails,
    DomainAccountInventoryResultDetails,
} from './types';
import * as types from './mutation-types';
import { ErrorDetail } from '../../types';

// エラーをクリアする
const resetErrors = (state: AccountInventoryResultState): void => {
    const searchState = state.search;
    searchState.error = false;
    searchState.errorCode = undefined;
    searchState.errorMsg = undefined;

    const showState = state.show;
    showState.error = false;
    showState.errorCode = undefined;
    showState.errorMsg = undefined;

    const nodeItemsState = state.nodeItems;
    nodeItemsState.error = false;
    nodeItemsState.errorCode = undefined;
    nodeItemsState.errorMsg = undefined;

    const domainItemsState = state.domainItems;
    domainItemsState.error = false;
    domainItemsState.errorCode = undefined;
    domainItemsState.errorMsg = undefined;

    const diffState = state.diff;
    diffState.error = false;
    diffState.errorTitle = undefined;
    diffState.errorDetails = undefined;

    const retryState = state.retry;
    retryState.error = false;
    retryState.errorTitle = undefined;
    retryState.errorDetails = undefined;

    const csvState = state.csv;
    csvState.error = false;
    csvState.errorTitle = undefined;
    csvState.errorDetails = undefined;
};

export const mutations: MutationTree<AccountInventoryResultState> = {
    // 棚卸結果一覧検索系
    [types.SEARCH_START](state) {
        resetErrors(state);
        const searchState = state.search;
        searchState.isProcessing = true;
        searchState.isFetchSuccess = false;
    },

    [types.SEARCH_SUCCESS](state, payload: { count: number; inventoryResults: Array<AccountInventoryResult> }) {
        state.inventoryResultCount = payload.count;
        state.inventoryResults = payload.inventoryResults;

        const searchState = state.search;
        searchState.isProcessing = false;
        searchState.isFetchSuccess = true;
    },

    [types.SEARCH_FAILURE](state, payload: { code: string; message: string }) {
        const searchState = state.search;
        searchState.errorCode = payload.code;
        searchState.errorMsg = payload.message;
        searchState.error = true;
        searchState.isProcessing = false;
        searchState.isFetchSuccess = false;
    },

    // 棚卸結果詳細基本情報取得系
    [types.SHOW_START](state) {
        resetErrors(state);
        const showState = state.show;
        showState.isProcessing = true;
        showState.isFetchSuccess = false;
    },

    [types.SHOW_SUCCESS](state, payload: AccountInventoryResultBasicInformation) {
        const showState = state.show;
        state.inventoryResultBasicInformation = payload;

        showState.isProcessing = false;
        showState.isFetchSuccess = true;
    },

    [types.SHOW_FAILURE](state, payload: { code: string; message: string }) {
        const showState = state.show;
        showState.errorCode = payload.code;
        showState.errorMsg = payload.message;
        showState.error = true;
        showState.isProcessing = false;
        showState.isFetchSuccess = false;
    },

    // 棚卸結果詳細検索系 ノードグループ
    [types.NODE_ITEMS_START](state) {
        resetErrors(state);
        const nodeItemsState = state.nodeItems;
        nodeItemsState.isProcessing = true;
        nodeItemsState.isFetchSuccess = false;
    },

    [types.NODE_ITEMS_SUCCESS](
        state,
        payload: { count: number; inventoryResultDetails: Array<AccountInventoryResultDetails> }
    ) {
        state.inventoryResultDetailsCount = payload.count;
        state.inventoryResultDetails = payload.inventoryResultDetails;

        const nodeItemsState = state.nodeItems;
        nodeItemsState.isProcessing = false;
        nodeItemsState.isFetchSuccess = true;
    },

    [types.NODE_ITEMS_FAILURE](state, payload: { code: string; message: string }) {
        const nodeItemsState = state.nodeItems;
        nodeItemsState.errorCode = payload.code;
        nodeItemsState.errorMsg = payload.message;
        nodeItemsState.error = true;
        nodeItemsState.isProcessing = false;
        nodeItemsState.isFetchSuccess = false;
    },

    // 棚卸結果詳細検索系 ノードドメイン
    [types.DOMAIN_ITEMS_START](state) {
        resetErrors(state);
        const domainItemsState = state.domainItems;
        domainItemsState.isProcessing = true;
        domainItemsState.isFetchSuccess = false;
    },

    [types.DOMAIN_ITEMS_SUCCESS](
        state,
        payload: { count: number; domainInventoryResultDetails: Array<DomainAccountInventoryResultDetails> }
    ) {
        state.domainInventoryResultDetailsCount = payload.count;
        state.domainInventoryResultDetails = payload.domainInventoryResultDetails;

        const domainItemsState = state.domainItems;
        domainItemsState.isProcessing = false;
        domainItemsState.isFetchSuccess = true;
    },

    [types.DOMAIN_ITEMS_FAILURE](state, payload: { code: string; message: string }) {
        const domainItemsState = state.domainItems;
        domainItemsState.errorCode = payload.code;
        domainItemsState.errorMsg = payload.message;
        domainItemsState.error = true;
        domainItemsState.isProcessing = false;
        domainItemsState.isFetchSuccess = false;
    },

    // 棚卸結果差分取得系
    [types.DIFF_START](state) {
        resetErrors(state);
        const diffState = state.diff;
        diffState.isProcessing = true;
    },

    [types.DIFF_SUCCESS](state) {
        const diffState = state.diff;
        diffState.isProcessing = false;
    },

    [types.DIFF_FAILURE](state, payload: { errorTitle: string; errorDetails: Array<ErrorDetail> }) {
        const diffState = state.diff;
        diffState.errorTitle = payload.errorTitle;
        diffState.errorDetails = payload.errorDetails;
        diffState.error = true;
        diffState.isProcessing = false;
    },

    // 棚卸再実行系
    [types.RETRY_START](state) {
        resetErrors(state);
        const retryState = state.retry;
        retryState.isProcessing = true;
    },

    [types.RETRY_SUCCESS](state) {
        const retryState = state.retry;
        retryState.isProcessing = false;
    },

    [types.RETRY_FAILURE](state, payload: { errorTitle: string; errorDetails: Array<ErrorDetail> }) {
        const retryState = state.retry;
        retryState.errorTitle = payload.errorTitle;
        retryState.errorDetails = payload.errorDetails;
        retryState.error = true;
        retryState.isProcessing = false;
    },

    // CSV出力系
    [types.CSV_OUTPUT_START](state) {
        resetErrors(state);
        const csvState = state.csv;
        csvState.isProcessing = true;
    },

    [types.CSV_OUTPUT_SUCCESS](state) {
        const csvState = state.csv;
        csvState.isProcessing = false;
    },

    [types.CSV_OUTPUT_FAILURE](state, payload: { errorTitle: string; errorDetails: Array<ErrorDetail> }) {
        const csvState = state.csv;
        csvState.errorTitle = payload.errorTitle;
        csvState.errorDetails = payload.errorDetails;
        csvState.error = true;
        csvState.isProcessing = false;
    },

    // FATALエラー系
    [types.FATAL_FAILURE](state) {
        const searchState = state.search;
        searchState.isProcessing = false;
        searchState.isFetchSuccess = false;
    },

    [types.CLEAR_ALL](state) {
        resetErrors(state);
        state.inventoryResults = undefined;
        state.inventoryResultCount = 0;
    },
};
